import React, { useRef, useState, useCallback } from 'react'
import throttle from 'lodash/throttle'
import { isServerEnvironment } from '../environment'

export interface ScrollInfo {
    x: {
        percentage: number | null
        value: number
        total: number
        className: string
    }
    y: {
        percentage: number | null
        value: number
        total: number
        className: string
    }
}

// Edge has a bug where scrollHeight is 1px bigger than clientHeight when there's no scroll.
export const isEdge = !isServerEnvironment()
    ? /Edge\/\d./i.test(navigator.userAgent)
    : false

export function useScrollInfo<T extends HTMLElement>(): [
    ScrollInfo,
    any,
    React.RefObject<T>,
] {
    const [scroll, setScroll] = useState<ScrollInfo>({
        x: {
            percentage: 0,
            value: 0,
            total: 0,
            className: '',
        },
        y: {
            percentage: 0,
            value: 0,
            total: 0,
            className: '',
        },
    })
    const ref = useRef<T>(null)
    const throttleTime = 500
    function update() {
        const element = ref.current
        if (!element) {
            return
        }
        let maxY = element.scrollHeight - element.clientHeight
        const maxX = element.scrollWidth - element.clientWidth
        // Edge has a bug where scrollHeight is 1px bigger than clientHeight when there's no scroll.
        if (isEdge && maxY === 1 && element.scrollTop === 0) {
            maxY = 0
        }
        const percentageY = maxY !== 0 ? element.scrollTop / maxY : null
        const percentageX = maxX !== 0 ? element.scrollLeft / maxX : null
        let classNameY = 'no-scroll-y'
        if (percentageY === 0) {
            classNameY = 'scroll-top'
        } else if (percentageY === 1) {
            classNameY = 'scroll-bottom'
        } else if (percentageY) {
            classNameY = 'scroll-middle-y'
        }
        let classNameX = 'no-scroll-x'
        if (percentageX === 0) {
            classNameX = 'scroll-left'
        } else if (percentageX === 1) {
            classNameX = 'scroll-right'
        } else if (percentageX) {
            classNameX = 'scroll-middle-x'
        }
        const scrollInfo = {
            x: {
                percentage: percentageX,
                value: element.scrollLeft,
                total: maxX,
                className: classNameX,
            },
            y: {
                percentage: percentageY,
                value: element.scrollTop,
                total: maxY,
                className: classNameY,
            },
        }
        setScroll(scrollInfo)
    }
    const throttledUpdate = throttle(update, throttleTime)
    const setRef = useCallback(
        (node) => {
            if (node) {
                node.addEventListener('scroll', throttledUpdate)
                window.addEventListener('resize', throttledUpdate)
                ;(ref as any).current = node
                throttledUpdate()
            } else if (ref.current) {
                ref.current.removeEventListener('scroll', throttledUpdate)
                window.removeEventListener('resize', throttledUpdate)
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )
    return [scroll, setRef, ref]
}
